import React, { useState, useEffect } from 'react';
import TextMeApp from "@/common/TextMeApp";
import withOptionalAuth from "@/components/withOptionalAuth";
import MainLayout from "@/layouts/MainLayout";
import { NextPageWithLayout } from "../_app.page";
import BrowseConcept from "./_components/BrowseConcept";
import ChoosePreferences from "./_components/ChoosePreferences";
import CourseGenerationLoader from "./_components/CourseGenerationLoader";
import GeneratedCourse from "./_components/GeneratedCourse";
import LearnDashboard from "./_components/LearnDashboard";
import { fetchGeneratedCourseSnapshots, GeneratedCourseRecord } from "@/utils/supabase";
import { useAuthUser } from '@/hooks/useAuthUser';
import { firebaseAuth } from '@/lib/Firebase';
import { useLoginModal } from '@/common/LoginModal';

enum LearnStep {
  WELCOME = 'welcome',
  CHOOSE_STOCK = 'choose-stock',
  CHOOSE_PREFERENCES = 'choose-preferences',
  GENERATING = 'generating',
  READY = 'ready', // Course is ready to view
}

function LearnIndexPage() {
  const { data: authUser } = useAuthUser(['authUser'], firebaseAuth);
  const { requireAuth } = useLoginModal();
  const [step, setStep] = useState<LearnStep>(LearnStep.WELCOME);
  const [courses, setCourses] = useState<GeneratedCourseRecord[]>([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState<{ stockName: string; ticker: string; isIndustry: boolean } | null>(null);
  const [isCourseReady, setIsCourseReady] = useState(false);
  const [storedCourseId, setStoredCourseId] = useState<string | undefined>(undefined);
  const [storedCoursePayload, setStoredCoursePayload] = useState<any | undefined>(undefined);
  const [isNewCourse, setIsNewCourse] = useState(false);

  useEffect(() => {
    async function loadCourses() {
      try {
        const data = await fetchGeneratedCourseSnapshots(authUser?.uid, 20);
        setCourses(data);
      } catch (error) {
        console.error('Error loading courses:', error);
      } finally {
        setIsLoadingCourses(false);
      }
    }
    if (authUser?.uid) {
      loadCourses();
    } else {
      // For unauthenticated users, just stop loading
      setIsLoadingCourses(false);
    }
  }, [step, authUser?.uid]); // Reload when returning to welcome or after build or when auth user is available

  const handleStockSelect = (stockName: string, ticker: string, isIndustry: boolean) => {
    setSelectedAsset({ stockName, ticker, isIndustry });
    setStep(LearnStep.CHOOSE_PREFERENCES);
  };

  const handleBack = () => {
    if (step === LearnStep.CHOOSE_PREFERENCES) {
      setStep(LearnStep.CHOOSE_STOCK);
    } else {
      setStep(LearnStep.WELCOME);
    }
  };

  const handleGenerateNew = () => {
    setStep(LearnStep.CHOOSE_STOCK);
  };

  const handleSelectCourse = (course: GeneratedCourseRecord) => {
    setSelectedAsset({
      stockName: course.course_payload.metadata?.stockName || course.course_title,
      ticker: course.course_payload.metadata?.ticker || '',
      isIndustry: !!course.course_payload.metadata?.isIndustry
    });
    setStoredCourseId(course.id);
    setStoredCoursePayload(course.course_payload);
    setIsNewCourse(false);
    setStep(LearnStep.READY);
  };

  const handleBuild = ({ id, payload }: { id: string; payload: any }) => {
    console.log('Building course with id:', id);
    setStoredCourseId(id);
    setStoredCoursePayload(payload);
    setStep(LearnStep.GENERATING);
  };

  const handleCourseReady = () => {
    console.log('Course is fully ready and quiz complete!');
    setIsNewCourse(true);
    setStep(LearnStep.READY);
  };

  const handleSkip = () => {
    console.log('Skipping loader...');
    setIsNewCourse(true);
    setStep(LearnStep.READY);
  };

  return (
    <div className="py-6">
      <div className="flex flex-col max-w-screen-lg lg:mx-auto">
        {step === LearnStep.WELCOME && (
          <LearnDashboard
            courses={courses}
            isLoading={isLoadingCourses}
            onGenerateNew={handleGenerateNew}
            onSelectCourse={handleSelectCourse}
            isAuthenticated={!!authUser}
          />
        )}
        {step === LearnStep.CHOOSE_STOCK && (
          <BrowseConcept onSelect={handleStockSelect} onBack={handleBack} />
        )}
        {step === LearnStep.CHOOSE_PREFERENCES && selectedAsset && (
          <ChoosePreferences
            stockName={selectedAsset.stockName}
            ticker={selectedAsset.ticker}
            isIndustry={selectedAsset.isIndustry}
            onBack={handleBack}
            onBuild={handleBuild}
            userId={authUser?.uid}
            onRequireAuth={requireAuth}
          />
        )}
        {step === LearnStep.GENERATING && selectedAsset && (
          <CourseGenerationLoader
            stockName={selectedAsset.stockName}
            ticker={selectedAsset.ticker}
            isCourseReady={isCourseReady}
            onCourseReady={handleCourseReady}
            onSkip={handleSkip}
            courseId={storedCourseId}
            userId={authUser?.uid}
          />
        )}
        {step === LearnStep.READY && selectedAsset && (
          <GeneratedCourse
            stockName={selectedAsset.stockName}
            ticker={selectedAsset.ticker}
            isIndustry={selectedAsset.isIndustry}
            onBack={handleBack}
            storedCourseId={storedCourseId}
            storedCoursePayload={storedCoursePayload}
            isNewCourse={isNewCourse}
            onUpdatePayload={(newPayload) => setStoredCoursePayload(newPayload)}
            userId={authUser?.uid}
          />
        )}
        <TextMeApp />
      </div>
    </div>
  );
}

const LearnIndexPageOptionalAuth = withOptionalAuth(LearnIndexPage) as NextPageWithLayout;

export default LearnIndexPageOptionalAuth;

LearnIndexPageOptionalAuth.getLayout = MainLayout;
